// Theme (DESIGN_SPEC §3). The inline boot script in index.html applies the same rules before
// first paint; this module owns the theme afterwards.
//   Precedence: ?theme=light|dark (this page load only) > stored "light" | "dark" >
//   stored "system" (follows prefers-color-scheme, live) > light.
//   setPref(p)      explicit choice: stores p (including "system"), drops the ?theme= override
//                   (and removes it from the URL with history.replaceState), applies as a hard cut
//   toggleTheme()   flips the visible theme light ↔ dark and stores it
//   useTheme()      [pref, theme, setPref] — also readable as { pref, theme, override, setPref, toggle }
// Swaps are hard cuts: data-theme-switching is set for two frames (base CSS kills transitions).
// Another tab changing the stored pref re-applies here (storage event); the meta theme-color and
// color-scheme follow every change.
import { useMemo, useSyncExternalStore } from "react";

export type ThemePref = "light" | "dark" | "system";
type Theme = "light" | "dark";

const THEME_KEY = "malbox-theme";
const THEME_COLOR: Record<Theme, string> = { light: "#f2f3f5", dark: "#0b1219" };

const hasDom = typeof window !== "undefined" && typeof document !== "undefined";

function media(): MediaQueryList | null {
  return hasDom && typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;
}

/** The same test the boot script runs on location.search. */
function parseThemeParam(search: string): Theme | null {
  const m = /[?&]theme=(light|dark)(?:&|$)/.exec(search);
  return m ? (m[1] as Theme) : null;
}

// Captured once per document: router navigations drop the query string, but the override
// lasts for the whole load until the viewer picks a theme.
let override: Theme | null = hasDom ? parseThemeParam(window.location.search) : null;

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "light";
  } catch {
    return "light"; // storage blocked (privacy mode, sandboxed iframe)
  }
}

function resolveTheme(p: ThemePref): Theme {
  return p === "system" ? (media()?.matches ? "dark" : "light") : p;
}

/** The theme on screen now (override first). */
function currentTheme(): Theme {
  return override ?? resolveTheme(readPref());
}


let switching = 0;

function apply(theme: Theme): void {
  if (!hasDom) return;
  const root = document.documentElement;
  if (root.dataset.theme !== theme) {
    const token = ++switching;
    root.setAttribute("data-theme-switching", "");
    root.dataset.theme = theme;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (token === switching) root.removeAttribute("data-theme-switching");
      }),
    );
  }
  root.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
}

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function dropOverride(): void {
  override = null;
  if (!hasDom) return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("theme")) return;
  url.searchParams.delete("theme");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

function setPref(p: ThemePref): void {
  try {
    localStorage.setItem(THEME_KEY, p);
  } catch {
    /* non-persistent is fine */
  }
  dropOverride();
  apply(resolveTheme(p));
  emit();
}

function toggleTheme(): void {
  setPref(currentTheme() === "dark" ? "light" : "dark");
}

// Global listeners, installed once: the theme must follow the OS ("system") and other tabs
// even when no theme control is mounted.
if (hasDom) {
  media()?.addEventListener("change", () => {
    if (!override && readPref() === "system") apply(resolveTheme("system"));
    emit();
  });
  window.addEventListener("storage", (e) => {
    if (e.key !== THEME_KEY && e.key !== null) return;
    apply(currentTheme());
    emit();
  });
  apply(currentTheme());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

const snapshot = (): string => `${readPref()}:${currentTheme()}:${override ?? ""}`;
const serverSnapshot = (): string => "light:light:";

type ThemeState = readonly [ThemePref, Theme, (p: ThemePref) => void] & {
  pref: ThemePref;
  theme: Theme;
  override: Theme | null;
  setPref: (p: ThemePref) => void;
  toggle: () => void;
};

/** Re-renders on a toggle, an OS change (for "system") or another tab. */
export function useTheme(): ThemeState {
  const s = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  return useMemo(() => {
    const [pref, theme, ov] = s.split(":") as [ThemePref, Theme, string];
    const tuple = [pref, theme, setPref] as const;
    return Object.assign(tuple, { pref, theme, override: (ov || null) as Theme | null, setPref, toggle: toggleTheme });
  }, [s]);
}
